import React, { useEffect, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { format, isToday, isTomorrow, startOfDay } from "date-fns";

const PER_PAGE = 10;

const statusSx = (s) =>
  ({
    paid: { bgcolor: "#d1fae5", color: "#065f46" },
    pending: { bgcolor: "#fef3c7", color: "#92400e" },
    failed: { bgcolor: "#fee2e2", color: "#991b1b" },
  }[s] || { bgcolor: "#f3f4f6", color: "#374151" });

function dayLabel(date) {
  if (isToday(date)) return "Today";
  if (isTomorrow(date)) return "Tomorrow";
  return format(date, "MMM d, yyyy");
}

function groupByDay(items) {
  const map = new Map();
  items.forEach((b) => {
    const date = new Date(b.time);
    const key = format(date, "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, { date, label: dayLabel(date), items: [] });
    map.get(key).items.push(b);
  });
  return [...map.values()];
}

export default function AdminBookingSchedule() {
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const todayStart = startOfDay(new Date()).toISOString();
        const snap = await getDocs(
          query(collection(db, "booking-list"), where("time", ">=", todayStart))
        );

        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) => b.time && b.status === "paid")
          .sort((a, b) => new Date(a.time) - new Date(b.time)); // today first, earliest slot first

        setAllDocs(docs);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: "#194049" }} />
      </Box>
    );
  }

  if (allDocs.length === 0) {
    return (
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", py: 8, textAlign: "center" }}>
        <Typography sx={{ color: "#9ca3af", fontSize: 14 }}>No upcoming bookings</Typography>
      </Box>
    );
  }

  const totalPages = Math.ceil(allDocs.length / PER_PAGE);
  const paged = allDocs.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const groups = groupByDay(paged);

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 3 }}>
        {allDocs.length} upcoming booking{allDocs.length !== 1 ? "s" : ""}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {groups.map(({ label, date, items }) => (
          <Box key={label}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                {label}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
                {format(date, "EEEE, MMMM d")}
              </Typography>
              <Chip
                label={`${items.length} booking${items.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ fontSize: 11, height: 20, bgcolor: "#f3f4f6", color: "#6b7280" }}
              />
            </Box>

            <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
              <Box sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
                      {["Time", "Category", "Service", "Status", "Reference"].map((h) => (
                        <th
                          key={h}
                          style={{
                            padding: "10px 16px",
                            textAlign: "left",
                            fontSize: 11,
                            color: "#6b7280",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((b) => {
                      const slotTime = new Date(b.time);
                      const timeLabel = format(slotTime, slotTime.getMinutes() === 0 ? "ha" : "h:mma").toLowerCase();
                      return (
                        <tr key={b.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 600, color: "#111827", whiteSpace: "nowrap" }}>
                            {timeLabel}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                            {b.category || "—"}
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 13, color: "#374151" }}>
                            {b.service || "—"}
                          </td>
                          <td style={{ padding: "12px 16px" }}>
                            <Chip
                              label={b.status || "pending"}
                              size="small"
                              sx={{ ...statusSx(b.status), fontSize: 11, height: 22, textTransform: "capitalize" }}
                            />
                          </td>
                          <td style={{ padding: "12px 16px", fontSize: 12, color: "#6b7280", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                            {b.paymentIntentId || "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </Box>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Pagination */}
      <Box
        sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          mt: 3, px: 1,
        }}
      >
        <Typography sx={{ fontSize: 12, color: "#6b7280" }}>
          Page {page + 1} of {Math.max(totalPages, 1)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {["Prev", "Next"].map((label) => (
            <Box
              key={label}
              onClick={() => setPage((p) => label === "Prev" ? Math.max(0, p - 1) : Math.min(totalPages - 1, p + 1))}
              sx={{
                px: 2, py: 0.75, fontSize: 12, borderRadius: "6px",
                border: "1px solid #e5e7eb", cursor: "pointer", bgcolor: "#fff", color: "#374151",
                "&:hover": { bgcolor: "#f9fafb" },
                opacity: (label === "Prev" && page === 0) || (label === "Next" && page >= totalPages - 1) ? 0.4 : 1,
                pointerEvents: (label === "Prev" && page === 0) || (label === "Next" && page >= totalPages - 1) ? "none" : "auto",
              }}
            >
              {label}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
